import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { createOperation, enqueueOperation, getDeviceId, loadOfflineReference, saveOfflineReference } from "@/lib/offlineSync";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { captureSelectionPermitted, initialCaptureSelection, templatesForCategory } from "@/lib/offlineQuickActions";

const MODES = ["incident", "inventory", "form"];

export default function OfflineCapturePage() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const initialSelection = useMemo(() => initialCaptureSelection(searchParams), [searchParams]);
  const [mode, setMode] = useState(initialSelection.mode);
  const [reference, setReference] = useState({ actions: [], inventory_items: [], form_templates: [] });
  const [draft, setDraft] = useState({ priority: "medium", category: "other", quantity: "", answers: {} });
  const templates = useMemo(
    () => templatesForCategory(reference.form_templates, initialSelection.category),
    [reference.form_templates, initialSelection.category],
  );
  const selectedTemplate = useMemo(() => templates.find((row) => row.id === draft.template_id), [draft.template_id, templates]);
  const authorized = captureSelectionPermitted(mode, initialSelection.category, reference.actions);

  useEffect(() => {
    if (!user?.id) return;
    api.get("/offline-sync/reference-data").then(async (response) => {
      setReference(response.data || {});
      await saveOfflineReference(user.id, "field-reference", response.data || {});
    }).catch(async () => {
      const cached = await loadOfflineReference(user.id, "field-reference").catch(() => null);
      if (cached?.data) setReference(cached.data);
    });
  }, [user?.id]);

  useEffect(() => {
    if (mode !== "form" || draft.template_id || templates.length !== 1) return;
    setDraft((current) => ({ ...current, template_id: templates[0].id, answers: {} }));
  }, [draft.template_id, mode, templates]);

  const save = async () => {
    if (!authorized) return toast.error(t("common.permission_denied", { defaultValue: "This action is not available for your account." }));
    let endpoint;
    let payload;
    if (mode === "incident") {
      endpoint = "/incidents";
      payload = { category: draft.category || "other", priority: draft.priority || "medium", title: draft.title || "", description: draft.description || "", location: draft.location || "" };
      if (!payload.title.trim()) return toast.error(t("offline.capture.title_required"));
    } else if (mode === "inventory") {
      endpoint = "/inventory-usage";
      payload = { item_id: draft.item_id || "", quantity: Number(draft.quantity), type: "stock_out", reason: draft.reason || "Field use", notes: draft.notes || "" };
      if (!payload.item_id || !Number.isFinite(payload.quantity) || payload.quantity <= 0) return toast.error(t("offline.capture.inventory_required"));
    } else {
      endpoint = "/forms/submissions";
      payload = { template_id: draft.template_id || "", answers: draft.answers || {}, location: draft.location || "", notes: draft.notes || "" };
      if (!payload.template_id) return toast.error(t("offline.capture.form_required"));
    }
    try {
      const operation = createOperation({
        userId: user.id, deviceId: getDeviceId(), module: mode, action: "create", endpoint, payload,
        serverKnownRevision: mode === "form" ? (selectedTemplate?.updated_at || selectedTemplate?.created_at || null) : null,
      });
      await enqueueOperation(operation);
      setDraft({ priority: "medium", category: "other", quantity: "", answers: {} });
      window.dispatchEvent(new CustomEvent("ajfarm:sync-updated"));
      toast.success(navigator.onLine ? t("offline.status.pending") : t("offline.saved_device"));
    } catch (error) {
      toast.error(error.message || t("common.error"));
    }
  };

  return <div className="space-y-4 pb-6" data-testid="offline-field-capture">
    <div><div className="tiny-label">{t("offline.capture.eyebrow")}</div><h1 className="text-2xl font-bold">{t("offline.capture.title")}</h1></div>
    <div className="grid grid-cols-3 gap-2">{MODES.map((item) => <Button key={item} variant={mode === item ? "default" : "outline"} className="h-12 px-2 text-xs sm:text-sm" onClick={() => setMode(item)}>{t(`offline.capture.${item}`)}</Button>)}</div>
    <Card className="p-4 space-y-3 overflow-hidden">
      {mode === "incident" && <>
        <Input value={draft.title || ""} onChange={(e) => setDraft({ ...draft, title: e.target.value })} placeholder={t("offline.capture.incident_title")} />
        <select className="h-11 w-full rounded-md border bg-background px-3" value={draft.category} onChange={(e) => setDraft({ ...draft, category: e.target.value })}>
          {["broken_equipment", "sick_animal", "escaped_animal", "low_feed", "farm_hazard", "vehicle_issue", "utility_issue", "biosecurity", "other"].map((value) => <option key={value} value={value}>{value.replaceAll("_", " ")}</option>)}
        </select>
        <select className="h-11 w-full rounded-md border bg-background px-3" value={draft.priority} onChange={(e) => setDraft({ ...draft, priority: e.target.value })}>{["low", "medium", "high", "urgent"].map((value) => <option key={value}>{value}</option>)}</select>
        <textarea className="min-h-28 w-full rounded-md border bg-background p-3" value={draft.description || ""} onChange={(e) => setDraft({ ...draft, description: e.target.value })} placeholder={t("offline.capture.details")} />
      </>}
      {mode === "inventory" && <>
        <select className="h-11 w-full rounded-md border bg-background px-3" value={draft.item_id || ""} onChange={(e) => setDraft({ ...draft, item_id: e.target.value })}>
          <option value="">{t("offline.capture.choose_item")}</option>{(reference.inventory_items || []).map((item) => <option key={item.id} value={item.id}>{item.name} · {item.unit || "unit"}</option>)}
        </select>
        <Input type="number" min="0" step="any" value={draft.quantity || ""} onChange={(e) => setDraft({ ...draft, quantity: e.target.value })} placeholder={t("offline.capture.quantity")} />
        <Input value={draft.reason || ""} onChange={(e) => setDraft({ ...draft, reason: e.target.value })} placeholder={t("offline.capture.reason")} />
      </>}
      {mode === "form" && <>
        <select className="h-11 w-full rounded-md border bg-background px-3" value={draft.template_id || ""} onChange={(e) => setDraft({ ...draft, template_id: e.target.value, answers: {} })}>
          <option value="">{t("offline.capture.choose_form")}</option>{templates.map((item) => <option key={item.id} value={item.id}>{item.title}</option>)}
        </select>
        {(selectedTemplate?.fields || []).map((field) => <label key={field.key} className="block text-sm"><span className="font-medium">{field.label}</span>
          {field.type === "checkbox" ? <input className="ml-3 h-5 w-5" type="checkbox" checked={Boolean(draft.answers?.[field.key])} onChange={(e) => setDraft({ ...draft, answers: { ...draft.answers, [field.key]: e.target.checked } })} />
            : <Input type={field.type === "number" || field.type === "scale" ? "number" : "text"} value={draft.answers?.[field.key] ?? ""} onChange={(e) => setDraft({ ...draft, answers: { ...draft.answers, [field.key]: e.target.value } })} />}
        </label>)}
      </>}
      <Button className="h-12 w-full" onClick={save} disabled={!authorized}>{t("offline.capture.save")}</Button>
      <p className="text-xs text-muted-foreground">{t("offline.capture.notice")}</p>
    </Card>
  </div>;
}
